import React, { useState, useRef, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, Building2, User, CreditCard,
  FileUp, ClipboardCheck, Loader2, FileCheck2, AlertCircle,
} from "lucide-react";
import { ASSETS } from "../mock";
import { useLanguage } from "../context/LanguageContext";
import { onboardingI18n } from "../data/onboardingI18n";
import { getCountries } from "../data/countries";
import { PANAMA_BANKS, OTHER_BANK } from "../data/banks";
import { compressImageIfNeeded } from "../utils/compressImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "rapidpay_onboarding_draft";

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const stepIcons = [Building2, User, CreditCard, FileUp, ClipboardCheck];

const inputCls =
  "w-full px-4 py-3 text-[14px] font-medium text-black bg-white border-2 border-black/10 rounded-xl focus:border-[#0fb013] focus:outline-none transition-colors duration-200";
const labelCls = "block text-[12px] font-bold uppercase tracking-wider text-black/50 mb-1.5";
const errCls = "mt-1 text-[12px] font-semibold text-[#ff603e]";

const Field = ({ label, error, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
    {error && <p className={errCls}>{error}</p>}
  </div>
);

const DocumentUploadRow = ({ doc, state, uploading, error, onSelect, o }) => {
  const inputRef = useRef(null);
  return (
    <div
      data-testid={`doc-row-${doc.id}`}
      className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-2xl border-2 transition-colors duration-200 ${
        state ? "border-[#0fb013]/40 bg-[#0fe814]/5" : error ? "border-[#ff603e]/40 bg-[#ff603e]/5" : "border-black/10 bg-white"
      }`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${state ? "bg-[#0fe814]" : "bg-black"}`}>
          {state ? <FileCheck2 size={18} className="text-black" /> : <FileUp size={18} className="text-[#0fe814]" />}
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-black leading-snug">{doc.label}</p>
          {state ? (
            <p className="text-[11px] font-semibold text-[#0fb013] truncate">{o.docs.uploaded}: {state.filename}</p>
          ) : error ? (
            <p className="text-[11px] font-semibold text-[#ff603e]">{error}</p>
          ) : (
            <p className="text-[11px] font-medium text-black/40">PDF, JPG, PNG · máx. 1.5MB</p>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        data-testid={`doc-input-${doc.id}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelect(f);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        data-testid={`doc-upload-btn-${doc.id}`}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className={`shrink-0 inline-flex items-center justify-center gap-2 text-[12px] font-bold px-5 py-2.5 rounded-full transition-colors duration-200 disabled:opacity-60 ${
          state
            ? "text-black border-2 border-black/15 hover:border-black bg-white"
            : "text-black bg-[#0fe814] hover:bg-[#0fb013] shadow-[0_4px_12px_rgba(15,232,20,0.3)]"
        }`}
      >
        {uploading ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            {o.docs.uploading}
          </>
        ) : state ? (
          o.docs.change
        ) : (
          o.docs.upload
        )}
      </button>
    </div>
  );
};

const Onboarding = () => {
  const { lang } = useLanguage();
  const o = onboardingI18n[lang] || onboardingI18n.es;
  const countries = useMemo(() => getCountries(lang), [lang]);
  const navigate = useNavigate();

  const [step, setStep] = useState(() => loadDraft()?.step ?? 0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [reference, setReference] = useState(null);
  const [resumed, setResumed] = useState(() => Boolean(loadDraft()));

  const [form, setForm] = useState(() => ({
    name: "", ruc: "", category: "", website: "",
    fullName: "", docId: "", nationality: "", email: "", phone: "",
    methods: [], volume: "", bank: "", bankOther: false, accountNumber: "", accountType: "",
    terms: false,
    ...(loadDraft()?.form || {}),
  }));

  const [documents, setDocuments] = useState(() => loadDraft()?.documents || {});
  const [docUploading, setDocUploading] = useState({});
  const [docErrors, setDocErrors] = useState({});

  useEffect(() => {
    if (reference) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, form, documents, savedAt: Date.now() }));
  }, [step, form, documents, reference]);

  const resetDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    window.location.reload();
  };

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const toggleMethod = (id) => {
    setForm((f) => ({
      ...f,
      methods: f.methods.includes(id) ? f.methods.filter((m) => m !== id) : [...f.methods, id],
    }));
    setErrors((e) => ({ ...e, methods: undefined }));
  };

  const uploadDoc = async (docId, file) => {
    setDocUploading((u) => ({ ...u, [docId]: true }));
    setDocErrors((e) => ({ ...e, [docId]: undefined }));
    setErrors((e) => ({ ...e, documents: undefined }));
    try {
      const prepared = await compressImageIfNeeded(file);
      const data = new FormData();
      data.append("doc_type", docId);
      data.append("file", prepared);
      const res = await axios.post(`${API}/onboarding/upload`, data);
      setDocuments((d) => ({ ...d, [docId]: { file_id: res.data.file_id, filename: res.data.filename } }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDocErrors((e) => ({ ...e, [docId]: typeof detail === "string" ? detail : o.docs.uploadError }));
    } finally {
      setDocUploading((u) => ({ ...u, [docId]: false }));
    }
  };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (form.name.trim().length < 2) e.name = o.required;
      if (form.ruc.trim().length < 3) e.ruc = o.required;
      if (!form.category) e.category = o.required;
      if (!form.terms) e.terms = o.step1.termsRequired;
    }
    if (step === 1) {
      if (form.fullName.trim().length < 3) e.fullName = o.required;
      if (form.docId.trim().length < 3) e.docId = o.required;
      if (form.nationality.trim().length < 2) e.nationality = o.required;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = o.invalidEmail;
      if (form.phone.trim().length < 6) e.phone = o.required;
    }
    if (step === 2) {
      if (form.methods.length === 0) e.methods = o.selectOne;
      if (!form.volume) e.volume = o.required;
      if (form.bank.trim().length < 2) e.bank = o.required;
      if (form.accountNumber.trim().length < 4) e.accountNumber = o.required;
      if (!form.accountType) e.accountType = o.required;
    }
    if (step === 3) {
      const missing = o.docs.items.some((d) => !documents[d.id]);
      if (missing) e.documents = o.docs.requiredAll;
    }
    if (step === 4 && !form.terms) e.terms = o.step1.termsRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep()) {
      setStep((s) => Math.min(s + 1, 4));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        business: {
          name: form.name.trim(),
          ruc: form.ruc.trim(),
          category: form.category,
          website: form.website.trim() || null,
        },
        representative: {
          full_name: form.fullName.trim(),
          doc_id: form.docId.trim(),
          nationality: form.nationality.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
        },
        payment: {
          methods: form.methods,
          monthly_volume: form.volume,
          bank: form.bank.trim(),
          account_number: form.accountNumber.trim(),
          account_type: form.accountType,
        },
        documents: Object.entries(documents).map(([doc_type, d]) => ({
          doc_type,
          file_id: d.file_id,
          filename: d.filename,
        })),
        terms_accepted: form.terms,
        language: lang,
      };
      const res = await axios.post(`${API}/onboarding`, payload);
      localStorage.removeItem(DRAFT_KEY);
      setReference(res.data.reference);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Onboarding submit error", err);
      setSubmitError(o.error);
    } finally {
      setSubmitting(false);
    }
  };

  const methodLabel = (id) => o.step3.methodOptions.find((m) => m.id === id)?.label || id;
  const stepContent = [o.step1, o.step2, o.step3, o.docs, o.step4][step];

  return (
    <div className="font-montserrat bg-[#fafcf9] min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]">
        <div className="max-w-3xl mx-auto px-5 flex items-center justify-between h-[64px] sm:h-[72px]">
          <Link to="/" className="flex items-center shrink-0">
            <img src={ASSETS.logo} alt="RapidPay" className="h-8 sm:h-9 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="onboarding-back-home"
            className="inline-flex items-center gap-2 text-[12px] sm:text-[13px] font-bold text-black px-4 sm:px-5 py-2 sm:py-2.5 rounded-full border-2 border-black/15 hover:border-black transition-colors duration-200"
          >
            <ArrowLeft size={15} />
            <span className="hidden sm:inline">{o.backHome}</span>
            <span className="sm:hidden">Inicio</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-5 py-8 sm:py-10 lg:py-14">
        {reference ? (
          /* ---------- Success ---------- */
          <div data-testid="onboarding-success" className="bg-white border border-black/[0.06] rounded-3xl p-8 sm:p-10 lg:p-14 text-center shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
            <div className="mx-auto w-20 h-20 rounded-full bg-[#0fe814]/15 flex items-center justify-center">
              <CheckCircle2 size={44} className="text-[#0fb013]" />
            </div>
            <h1 className="mt-6 font-extrabold text-[26px] sm:text-[28px] lg:text-[34px] tracking-tight text-black">{o.success.title}</h1>
            <p className="mt-3 text-[14px] font-medium text-black/55">{o.success.subtitle}</p>
            <div data-testid="onboarding-reference" className="mt-2 inline-block font-extrabold text-[22px] sm:text-[26px] text-[#0fb013] bg-[#0fe814]/10 px-6 py-2 rounded-full">
              {reference}
            </div>
            <p className="mt-6 text-[14px] leading-relaxed font-medium text-black/60 max-w-md mx-auto">{o.success.message}</p>
            <button
              onClick={() => navigate("/")}
              className="mt-8 inline-flex items-center gap-2 text-[14px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-8 py-4 rounded-full transition-colors duration-200 shadow-[0_6px_20px_rgba(15,232,20,0.35)]"
            >
              {o.success.cta}
            </button>
            <p className="mt-5 text-[12px] font-semibold text-black/50">
              {lang === "en" ? "Check your application status anytime at" : lang === "pt" ? "Consulte o status da sua solicitação em" : "Consulta el estado de tu solicitud en"}{" "}
              <Link to="/mi-cuenta" data-testid="success-portal-link" className="font-bold text-[#0fb013] underline">/mi-cuenta</Link>
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-extrabold text-[24px] sm:text-[28px] lg:text-[36px] tracking-tight text-black">{o.pageTitle}</h1>
            <p className="mt-2 text-[13px] sm:text-[14px] font-medium text-black/55">{o.pageSubtitle}</p>

            {resumed && (
              <div data-testid="resume-banner" className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 bg-[#0fe814]/10 border border-[#0fb013]/30 rounded-2xl px-4 py-3">
                <p className="flex-1 text-[13px] font-semibold text-black/75">{o.resume.text}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" data-testid="resume-reset" onClick={resetDraft} className="text-[12px] font-bold text-black/60 hover:text-black underline">{o.resume.reset}</button>
                  <button type="button" data-testid="resume-dismiss" onClick={() => setResumed(false)} className="text-[12px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-4 py-1.5 rounded-full transition-colors">{o.resume.continue}</button>
                </div>
              </div>
            )}

            {/* Stepper */}
            <div className="mt-7 sm:mt-8 flex items-center gap-1.5 sm:gap-2">
              {o.steps.map((label, i) => {
                const Icon = stepIcons[i];
                const active = i === step;
                const done = i < step;
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center gap-1.5 min-w-[44px] sm:min-w-[64px]">
                      <div
                        data-testid={`onboarding-step-indicator-${i}`}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${
                          done ? "bg-[#0fe814] border-[#0fe814] text-black"
                          : active ? "bg-black border-black text-[#0fe814]"
                          : "bg-white border-black/15 text-black/30"
                        }`}
                      >
                        {done ? <Check size={17} strokeWidth={3} /> : <Icon size={16} />}
                      </div>
                      <span className={`hidden sm:block text-[10px] font-bold uppercase tracking-wide text-center ${active || done ? "text-black" : "text-black/35"}`}>
                        {label}
                      </span>
                    </div>
                    {i < o.steps.length - 1 && (
                      <div className={`flex-1 h-[3px] rounded-full sm:mb-5 ${i < step ? "bg-[#0fe814]" : "bg-black/10"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* Mobile step label */}
            <p className="sm:hidden mt-3 text-[12px] font-bold uppercase tracking-wide text-[#0fb013]">
              {step + 1}/5 · {o.steps[step]}
            </p>

            {/* Card */}
            <div className="mt-6 sm:mt-8 bg-white border border-black/[0.06] rounded-3xl p-5 sm:p-7 lg:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
              <h2 className="font-bold text-[18px] sm:text-[20px] text-black">{stepContent.title}</h2>
              <p className="mt-1 text-[13px] font-medium text-black/50">{stepContent.desc}</p>

              <div className="mt-6 sm:mt-7 space-y-5">
                {step === 0 && (
                  <>
                    <Field label={o.step1.name} error={errors.name}>
                      <input data-testid="input-business-name" className={inputCls} placeholder={o.step1.namePh} value={form.name} onChange={(e) => set("name", e.target.value)} />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label={o.step1.ruc} error={errors.ruc}>
                        <input data-testid="input-business-ruc" className={inputCls} placeholder={o.step1.rucPh} value={form.ruc} onChange={(e) => set("ruc", e.target.value)} />
                      </Field>
                      <Field label={o.step1.category} error={errors.category}>
                        <select data-testid="input-business-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                          <option value="">{o.step1.categoryPh}</option>
                          {o.step1.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                    </div>
                    <Field label={o.step1.website}>
                      <input data-testid="input-business-website" className={inputCls} placeholder={o.step1.websitePh} value={form.website} onChange={(e) => set("website", e.target.value)} />
                    </Field>
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <button
                          type="button"
                          data-testid="terms-checkbox"
                          aria-checked={form.terms}
                          role="checkbox"
                          onClick={() => set("terms", !form.terms)}
                          className={`mt-0.5 shrink-0 w-6 h-6 rounded-md flex items-center justify-center border-2 transition-colors duration-200 ${
                            form.terms ? "bg-[#0fe814] border-[#0fe814]" : "border-black/25 bg-white"
                          }`}
                        >
                          {form.terms && <Check size={15} strokeWidth={3} className="text-black" />}
                        </button>
                        <span className="text-[13px] font-medium text-black/70">
                          {o.step1.termsPrefix}{" "}
                          <a
                            href="/terminos-y-condiciones"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="terms-link"
                            className="font-bold text-[#0fb013] underline hover:text-black"
                          >
                            {o.step1.termsLink}
                          </a>
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {step === 1 && (
                  <>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label={o.step2.fullName} error={errors.fullName}>
                        <input data-testid="input-rep-name" className={inputCls} placeholder={o.step2.fullNamePh} value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
                      </Field>
                      <Field label={o.step2.docId} error={errors.docId}>
                        <input data-testid="input-rep-docid" className={inputCls} placeholder={o.step2.docIdPh} value={form.docId} onChange={(e) => set("docId", e.target.value)} />
                      </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label={o.step2.nationality} error={errors.nationality}>
                        <select data-testid="input-rep-nationality" className={inputCls} value={form.nationality} onChange={(e) => set("nationality", e.target.value)}>
                          <option value="">{o.step2.nationalityPh}</option>
                          {countries.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
                        </select>
                      </Field>
                      <Field label={o.step2.email} error={errors.email}>
                        <input data-testid="input-rep-email" type="email" className={inputCls} placeholder={o.step2.emailPh} value={form.email} onChange={(e) => set("email", e.target.value)} />
                      </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label={o.step2.phone} error={errors.phone}>
                        <input data-testid="input-rep-phone" className={inputCls} placeholder={o.step2.phonePh} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                      </Field>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div>
                      <label className={labelCls}>{o.step3.methods}</label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {o.step3.methodOptions.map((m) => {
                          const selected = form.methods.includes(m.id);
                          return (
                            <button
                              key={m.id}
                              type="button"
                              data-testid={`method-option-${m.id}`}
                              onClick={() => toggleMethod(m.id)}
                              className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left text-[13px] font-bold transition-colors duration-200 ${
                                selected ? "border-[#0fb013] bg-[#0fe814]/10 text-black" : "border-black/10 bg-white text-black/60 hover:border-black/30"
                              }`}
                            >
                              <span className={`w-5 h-5 rounded-md flex items-center justify-center border-2 ${selected ? "bg-[#0fe814] border-[#0fe814]" : "border-black/20"}`}>
                                {selected && <Check size={13} strokeWidth={3} className="text-black" />}
                              </span>
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                      {errors.methods && <p className={errCls}>{errors.methods}</p>}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label={o.step3.volume} error={errors.volume}>
                        <select data-testid="input-payment-volume" className={inputCls} value={form.volume} onChange={(e) => set("volume", e.target.value)}>
                          <option value="">{o.step3.volumePh}</option>
                          {o.step3.volumeOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </Field>
                      <Field label={o.step3.bank} error={errors.bank}>
                        <select
                          data-testid="input-payment-bank-select"
                          className={inputCls}
                          value={form.bankOther ? OTHER_BANK : form.bank}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => ({ ...f, bankOther: v === OTHER_BANK, bank: v === OTHER_BANK ? "" : v }));
                            setErrors((er) => ({ ...er, bank: undefined }));
                          }}
                        >
                          <option value="">{o.step3.bankSelectPh}</option>
                          {PANAMA_BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
                          <option value={OTHER_BANK}>{o.step3.bankOther}</option>
                        </select>
                        {form.bankOther && (
                          <input data-testid="input-payment-bank" className={`${inputCls} mt-2`} placeholder={o.step3.bankPh} value={form.bank} onChange={(e) => set("bank", e.target.value)} autoFocus />
                        )}
                      </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label={o.step3.accountNumber} error={errors.accountNumber}>
                        <input data-testid="input-payment-account-number" className={inputCls} placeholder={o.step3.accountNumberPh} value={form.accountNumber} onChange={(e) => set("accountNumber", e.target.value)} />
                      </Field>
                      <Field label={o.step3.accountType} error={errors.accountType}>
                        <select data-testid="input-payment-account-type" className={inputCls} value={form.accountType} onChange={(e) => set("accountType", e.target.value)}>
                          <option value="">{o.step3.accountTypePh}</option>
                          {o.step3.accountTypes.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                      </Field>
                    </div>
                  </>
                )}

                {step === 3 && (
                  <>
                    <div className="space-y-3">
                      {o.docs.items.map((doc) => (
                        <DocumentUploadRow
                          key={doc.id}
                          doc={doc}
                          state={documents[doc.id]}
                          uploading={!!docUploading[doc.id]}
                          error={docErrors[doc.id]}
                          onSelect={(file) => uploadDoc(doc.id, file)}
                          o={o}
                        />
                      ))}
                    </div>
                    {errors.documents && (
                      <p data-testid="docs-error" className="flex items-center gap-2 text-[13px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-4 py-3 rounded-xl">
                        <AlertCircle size={16} />
                        {errors.documents}
                      </p>
                    )}
                  </>
                )}

                {step === 4 && (
                  <>
                    <div className="space-y-4">
                      {[
                        { title: o.step4.business, rows: [form.name, form.ruc, form.category, form.website].filter(Boolean) },
                        { title: o.step4.representative, rows: [form.fullName, form.docId, form.nationality, form.email, form.phone] },
                        { title: o.step4.payment, rows: [form.methods.map(methodLabel).join(", "), form.volume, form.bank, form.accountNumber, o.step3.accountTypes.find((a) => a.value === form.accountType)?.label].filter(Boolean) },
                        { title: o.docs.summaryLabel, rows: o.docs.items.map((d) => `${d.label}: ${documents[d.id]?.filename || "—"}`) },
                      ].map((block) => (
                        <div key={block.title} className="bg-[#fafcf9] border border-black/[0.06] rounded-2xl p-4 sm:p-5">
                          <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#0fb013]">{block.title}</h3>
                          <div className="mt-2 space-y-1">
                            {block.rows.map((r, i) => (
                              <p key={i} className="text-[13px] font-semibold text-black/75 break-words">{r}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {submitError && (
                      <p data-testid="onboarding-error" className="text-[13px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-4 py-3 rounded-xl">{submitError}</p>
                    )}
                  </>
                )}
              </div>

              {/* Nav buttons */}
              <div className="mt-8 sm:mt-9 flex items-center justify-between gap-3">
                {step > 0 ? (
                  <button
                    data-testid="onboarding-back"
                    onClick={() => setStep((s) => s - 1)}
                    className="inline-flex items-center gap-2 text-[13px] font-bold text-black px-5 sm:px-6 py-3 sm:py-3.5 rounded-full border-2 border-black/15 hover:border-black transition-colors duration-200"
                  >
                    <ArrowLeft size={15} />
                    {o.back}
                  </button>
                ) : <span />}

                {step < 4 ? (
                  <button
                    data-testid="onboarding-next"
                    onClick={next}
                    disabled={step === 0 && !form.terms}
                    className="inline-flex items-center gap-2 text-[13px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-6 sm:px-8 py-3 sm:py-3.5 rounded-full transition-colors duration-200 shadow-[0_5px_16px_rgba(15,232,20,0.35)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0fe814]"
                  >
                    {o.next}
                    <ArrowRight size={15} />
                  </button>
                ) : (
                  <button
                    data-testid="onboarding-submit"
                    onClick={submit}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 text-[13px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-6 sm:px-8 py-3 sm:py-3.5 rounded-full transition-colors duration-200 shadow-[0_5px_16px_rgba(15,232,20,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? o.submitting : o.submit}
                    {!submitting && <ArrowRight size={15} />}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Onboarding;
